/*
 * ====================================================================================================
 * WARNING: BACKEND-COPIED DTO FILE
 * THIS FILE IS COPIED FROM THE BACKEND.
 * DO NOT MODIFY THIS FILE MANUALLY IN FRONTEND CODE.
 * IT MAY BE DELETED, REGENERATED, OR REPLACED AT ANY TIME.
 * ====================================================================================================
 */

import { ParkAndRideDetailsDTO } from "./park-and-ride-details.dto";
import { ItineraryPricingEvaluationDTO } from "../pricing/itinerary-pricing-evaluation.dto";
import { PricingSegmentDTO } from "../pricing/pricing-segment.dto";
import {
    RentalLegPropertiesDTO,
    RentalReturnConstraint,
    RentalVehiclePropulsion,
    RentalVehicleType,
} from "../rental/rental-leg-properties.dto";
import { ItineraryOfferDTO } from "../sales/offer.dto";
import { LegProvider } from "../../../itinerary/leg-provider.enum";
import { LegType } from "../../../itinerary/leg-type.enum";
import { SegmentationProviderId } from "../../../itinerary/segmentation-provider.enum";

/** Supported itinerary sources exposed through itinerary payloads. */
export enum ItineraryType {
    "MOTIS" = "motis",
    "CAR" = "car",
    "BLABLADAILY" = "blabladaily",
    "BLABLACAR" = "blablacar",
}

/** Geometry dimensionality hint for encoded path payloads. */
export enum GeometryType {
    "TWO_DIMENSIONAL" = "2d",
    "THREE_DIMENSIONAL" = "3d",
}

export class ItineraryLegStepLocationDTO {

    /**
     * Latitude in decimal degrees.
     * Example: 46.2044
     */
    lat!: number;

    /**
     * Longitude in decimal degrees.
     * Example: 6.1432
     */
    lon!: number;

}

export class ItineraryLegStepStopDTO {

    /**
     * Provider stop identifier.
     * Example: 8503000
     */
    id!: string;

    /**
     * Provider stop display name.
     * Example: Lausanne
     */
    name?: string;

    /**
     * Optional parent stop identifier.
     * Example: 8503000:0:3
     */
    parent?: string;

}

export class ItineraryLegStepDTO {

    /** Type: ItineraryLegStepStopDTO */
    stop?: ItineraryLegStepStopDTO;

    /**
     * Human-readable waypoint name.
     * Example: Lausanne station
     */
    name?: string;

    /**
     * Platform or track information when available.
     * Example: Track 5
     */
    platform?: string;

    /**
     * Geographic location for this leg step.
     * Type: ItineraryLegStepLocationDTO
     */
    location!: ItineraryLegStepLocationDTO;

    /**
     * Operational datetime in ISO-8601.
     * Example: 2026-04-29T17:32:00.000Z
     */
    time!: string;

    /**
     * Scheduled datetime in ISO-8601 when different from realtime.
     * Example: 2026-04-29T17:30:00.000Z
     */
    scheduledTime?: string;

    /**
     * Transport mode labels attached to this waypoint.
     * Example: [ "rail", "intercity" ]
     * Type: [ String ]
     */
    modes?: string[];

    /** Type: ParkAndRideDetailsDTO */
    details?: ParkAndRideDetailsDTO;

}

/**
 * Normalized modality for a leg (MOTIS mode string is kept separately on {@link ItineraryLegDTO.mode}).
 * Data source or integration that produced the leg (distinct from segmentation ownership).
 * Registry key for segmentation, pricing and sales providers (Swiss CFF, carpool, rental, unknown, or completion). 
 */
export { LegProvider, LegType, SegmentationProviderId };

export { RentalReturnConstraint, RentalVehiclePropulsion, RentalVehicleType };

/** Single leg in the itinerary model; `priceEvaluationSegmentId` binds the leg to a {@link PricingSegmentDTO.id}. */
export class ItineraryLegDTO {

    /**
     * Canonical leg identifier formatted as `leg|{fromIsoTime}|{toIsoTime}`. `{fromIsoTime}` and `{toIsoTime}` are the leg endpoint datetimes in ISO-8601.
     * Example: SWAGGER_EXAMPLE_IDS.legTransit
     */
    id!: string;

    /**
     * Normalized leg modality.
     * Example: publicTransport
     * Enum: LegType
     */
    type!: LegType;

    /**
     * Provider that produced this leg.
     * Example: motis
     * Enum: LegProvider
     */
    provider!: LegProvider;

    /**
     * Source-provider identifier for this leg (for MOTIS legs this is usually the trip id), or null when unavailable.
     * Example: 1.TA.91-184-A-j26-1.1.H
     * Nullable: true
     * Required: true
     */
    providerId!: string | null;

    /**
     * Raw transport mode label from upstream provider.
     * Example: rail
     */
    mode!: string;

    /**
     * Leg duration in seconds.
     * Example: 1320
     */
    duration!: number;

    /**
     * Leg distance in meters when available.
     * Example: 52340
     */
    distance?: number;

    /**
     * Time markers aligned with geometry shape points when provided by upstream.
     * Example: [
     *             {
     *                 "shapeIndex": 0,
     *                 "time":       "2026-04-29T17:32:00.000Z",
     *                 "cumDist":    0,
     *             },
     *         ]
     * Type: [ Object ]
     */
    timeMarkers?: Array<{
        readonly "shapeIndex": number;
        readonly "time":       string;
        readonly "cumDist":    number;
    }>;

    /**
     * Encoded geometry metadata for the leg path.
     * Example: {
     *             "path":      "u~l~Fjk~uOwHJy@P",
     *             "precision": 5,
     *             "type":      GeometryType.TWO_DIMENSIONAL,
     *         }
     * Type: object
     */
    geometry?: {
        readonly "path":       string;
        readonly "precision"?: number;
        readonly "type"?:      GeometryType;
    };

    /**
     * Operating agency metadata for this leg.
     * Example: {
     *             "id":   "sbb",
     *             "name": "Swiss Federal Railways",
     *             "url":  "https://www.sbb.ch",
     *         }
     * Type: object
     */
    agency?: {
        readonly "id":   string;
        readonly "name": string;
        readonly "url"?: string;
    };

    /**
     * Optional ordered intermediate stops between leg endpoints.
     * Type: [ ItineraryLegStepDTO ]
     */
    intermediateStops?: ItineraryLegStepDTO[];

    /**
     * Transit route descriptors associated with this leg.
     * Example: {
     *             "type":      "rail",
     *             "shortName": "IC1",
     *             "longName":  "Geneve-Aeroport - St. Gallen",
     *             "headsign":  "St. Gallen",
     *             "color":     "d52b1e",
     *             "textColor": "ffffff",
     *         }
     * Type: object
     */
    route?: {
        readonly "type":       string;
        readonly "shortName"?: string;
        readonly "longName"?:  string;
        readonly "headsign"?:  string;
        readonly "color"?:     string;
        readonly "textColor"?: string;
    };

    /**
     * Realtime indicator for this leg.
     * Example: true
     */
    realTime?: boolean;

    /**
     * Whether bikes are allowed on this leg.
     * Example: false
     */
    bikesAllowed?: boolean;

    /**
     * Estimated toll in CHF when relevant for road segments.
     * Example: null
     * Nullable: true
     */
    toll?: number | null;

    /**
     * Rental-specific metadata for shared mobility legs. `vehicle.type`, `vehicle.propulsion`, and `returnConstraint` follow restricted enum values.
     * Example: {
     *             "vehicle": {
     *                 "type":       RentalVehicleType.BICYCLE,
     *                 "brand":      "Publibike",
     *                 "model":      "Classic",
     *                 "propulsion": RentalVehiclePropulsion.HUMAN,
     *             },
     *             "provider": {
     *                 "id":   "publibike",
     *                 "name": "PubliBike",
     *             },
     *             "uris":     { "default": "https://www.publibike.ch" },
     *             "stations": {
     *                 "from": "station-001",
     *                 "to":   "station-042",
     *             },
     *             "returnConstraint": RentalReturnConstraint.ANY_STATION,
     *         }
     * Type: object
     */
    rental?: RentalLegPropertiesDTO;

    /**
     * Leg-level price in CHF after pricing evaluation; null when unavailable.
     * Example: 14.5
     * Nullable: true
     */
    price?: number | null;

    /**
     * Owning pricing segment identifier assigned during pricing evaluation, formatted as `pricingSegment|{providerId}|{firstLegIndex}|{lastLegIndex}`.
     * Example: SWAGGER_EXAMPLE_IDS.pricingSegment
     * Nullable: true
     */
    priceEvaluationSegmentId?: string | null;

    /**
     * MOTIS leg start endpoint when available (copied from search transformation). Required for Swiss CFF Journey matching and stop resolution on full payloads; omitted on minimal client-supplied payloads.
     * Type: ItineraryLegStepDTO
     */
    from?: ItineraryLegStepDTO;

    /**
     * MOTIS leg end endpoint when available (copied from search transformation). Required for Swiss CFF Journey matching and stop resolution on full payloads; omitted on minimal client-supplied payloads.
     * Type: ItineraryLegStepDTO
     */
    to?: ItineraryLegStepDTO;

}

/** Full itinerary with optional `pricingSegments` after a pricing or segmentation pass. */
export class ItineraryDTO {

    /**
     * Canonical itinerary identifier formatted as `it|{variant}|{startIsoTime}|{endIsoTime}|{legsFingerprintHash20}`. `variant` is `base` or `completed`, and `legsFingerprintHash20` is the first 20 hex chars of a SHA-256 fingerprint.
     * Example: it|base|2026-04-29T17:30:00.000Z|2026-04-29T19:05:00.000Z|8f90c7f4b2d1e9a3c541
     */
    id!: string;

    /**
     * Itinerary start datetime in ISO-8601.
     * Example: 2026-04-29T17:30:00.000Z
     */
    startTime!: string;

    /**
     * Itinerary end datetime in ISO-8601.
     * Example: 2026-04-29T19:05:00.000Z
     */
    endTime!: string;

    /**
     * Finalization datetime in ISO-8601.
     * Example: 2026-04-29T17:31:02.113Z
     */
    finalizedAt!: string;

    /**
     * Creation timestamp in milliseconds.
     * Example: 1777483862113
     */
    timestamp!: number;

    /**
     * True when itinerary is finalized for downstream pricing/sales.
     * Example: true
     */
    finalized!: boolean;

    /**
     * Provider itinerary type label. Allowed values are restricted to known itinerary sources.
     * Example: ItineraryType.MOTIS
     * Enum: ItineraryType
     */
    type?: ItineraryType;

    /**
     * Total itinerary price in CHF; null when pricing is incomplete.
     * Example: 37.9
     * Nullable: true
     */
    price?: number | null;

    /**
     * Total itinerary duration in seconds.
     * Example: 5700
     */
    duration?: number;

    /**
     * Total walking duration in seconds.
     * Example: 640
     */
    walkTime?: number;

    /**
     * Ordered legs composing the itinerary.
     * Type: [ ItineraryLegDTO ]
     */
    legs!: ItineraryLegDTO[];

    /**
     * Reverse lookup from canonical leg identifier (`leg|{fromIsoTime}|{toIsoTime}`) to its index in `legs`.
     * Example: {
     *             "leg|2026-04-29T17:32:00.000Z|2026-04-29T18:24:00.000Z": 0,
     *             "leg|2026-04-29T18:34:00.000Z|2026-04-29T19:05:00.000Z": 1,
     *         }
     * Type: object
     */
    legIndexForId?: Record<string, number>;

    /**
     * Optional segment list generated by segmentation and enriched by pricing.
     * Type: [ PricingSegmentDTO ]
     */
    pricingSegments?: PricingSegmentDTO[];

    /**
     * Aggregate pricing evaluation for this itinerary.
     * Type: ItineraryPricingEvaluationDTO
     */
    pricingEvaluation?: ItineraryPricingEvaluationDTO;

    /**
     * Optional sales offer projection attached after offer evaluation.
     * Type: ItineraryOfferDTO
     */
    offer?: ItineraryOfferDTO;

}

/** Segment endpoint shape used by pricing and sales DTOs. */
export class SegmentEndpointDTO {

    /**
     * Optional endpoint identifier.
     * Example: 8503000
     */
    id?: string;

    /**
     * Optional endpoint display name.
     * Example: Lausanne
     */
    name?: string;

    /**
     * Example: {
     *             "lat": 46.2044,
     *             "lon": 6.1432,
     *         }
     * Type: object
     */
    location?: {
        readonly "lat": number;
        readonly "lon": number;
    };

    /**
     * Endpoint datetime in ISO-8601.
     * Example: 2026-04-29T17:30:00.000Z
     */
    time?: string;

}

/** Segment leg index range used by pricing and sales DTOs. */
export class SegmentLegRangeDTO {

    /**
     * First leg index included in the segment.
     * Example: 0
     */
    firstLegIndex!: number;

    /**
     * Last leg index included in the segment.
     * Example: 1
     */
    lastLegIndex!: number;

    /**
     * Ordered list of leg ids in this range.
     * Example: [ "leg|2026-04-29T08:15:00.000Z|2026-04-29T08:42:00.000Z", "leg|2026-04-29T08:45:00.000Z|2026-04-29T09:05:00.000Z" ]
     * Type: [ String ]
     */
    legs!: string[];

}
